import { useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { GrayCard } from "../../components/Card";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import { Image } from "../../components/Image";
import { Tooltip } from "../../components/Tooltip";
import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import {
  CheckHeading,
  CheckTable,
  CheckTableHeaderRow,
  CheckTableCell,
} from "./CheckStyledComponents";
import { FiAlertTriangle, FiCheck, FiClock, FiX } from "react-icons/fi";
import { FaQuestion } from "react-icons/fa";
import { DisplayAlias } from "../../utils/screeningMatch";
import { ApprovalCommentType } from "../../types/Individual";
import accept from "../../assets/accept.svg";
import reject from "../../assets/reject.svg";
import {
  formatSourceDate,
  formatPepDate,
  formatLabel,
  renderValue,
  DECISION_LABELS,
  DECISION_COLORS,
  CHECK_DECISION_LABEL_COLOR,
} from "./screeningResultHelpers";

/**
 * The decided-state banner for a screening check: who decided it, when, and
 * why. Uses the same accept/reject icons as the shared ApprovalComment (used
 * by ~20 other check types), but this layout — nested inside the tinted
 * decision shell — is specific to Screening's split layout.
 */
export const ScreeningApprovalBanner = ({
  data,
  status,
}: {
  data?: ApprovalCommentType;
  status?: string;
}) => {
  const isAccepted = status === CheckStatusEnum.ManuallyAccepted;
  const isRejected = status === CheckStatusEnum.ManuallyRejected;
  if (!isAccepted && !isRejected) return null;

  const name = [data?.first_name, data?.last_name].filter(Boolean).join(" ");
  const label = String(status).replace(/_/g, " ").toUpperCase();
  const labelColor = CHECK_DECISION_LABEL_COLOR[status as string] || "gray.60";

  return (
    <Flex gap="12" alignItems="flex-start">
      <Image
        src={isAccepted ? accept : reject}
        alt={isAccepted ? "Manually accepted" : "Manually rejected"}
        height={32}
        width={32}
      />
      <Box flex={1}>
        <Flex justifyContent="space-between" alignItems="baseline" gap="8">
          <Paragraph m="0px" fontSize={1}>
            <Box as="span" fontWeight="700" color={labelColor}>
              {label}
            </Box>
            {name && (
              <Box as="span" color="gray.80" fontWeight="600">
                {" "}
                · {name}
              </Box>
            )}
          </Paragraph>
          {data?.date && (
            <Paragraph
              m="0px"
              color="gray.50"
              fontSize={0}
              style={{ whiteSpace: "nowrap" }}
            >
              {DateTime.fromISO(data.date).toFormat("dd LLL, yyyy")}
            </Paragraph>
          )}
        </Flex>
        {data?.comment && (
          <Paragraph my={1} color="gray.80">
            &ldquo;{data.comment}&rdquo;
          </Paragraph>
        )}
      </Box>
    </Flex>
  );
};
/**
 * Compact styling for every table in this view.
 *
 * The shared table cell is sized for a page, at 16px text with 16px of vertical
 * padding, which is far too heavy for a match card holding several tables of up
 * to two hundred rows. Restyled once here rather than at each table, and kept
 * local so the legacy check views are untouched.
 */
export const DataTable = styled(CheckTable)`
  ${({ theme: { colors, fontSizes, fontWeights, space } }) => `
    font-size: ${fontSizes[1]};

    th, td {
      padding: ${space[1]};
      vertical-align: top;
    }

    thead td, tr:first-child td {
      font-size: ${fontSizes[0]};
      font-weight: ${fontWeights.semibold};
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: ${colors.gray[50]};
    }

    p {
      font-size: ${fontSizes[1]};
    }
  `}
`;

/** Label and value on one line, for the compact Information Used strip. */
export const InlineField = ({ label, value }: { label: string; value: any }) => (
  <Flex alignItems="baseline" gap="8">
    <Paragraph mb={0} mt={0} fontSize={0} color="gray.50">
      {label}
    </Paragraph>
    <Paragraph mb={0} mt={0} color="gray.80" fontWeight="500">
      {value === null || value === undefined || value === "" ? "-" : value}
    </Paragraph>
  </Flex>
);

export const MatchFlagIcon = ({ matched }: { matched?: boolean }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    width="20px"
    height="20px"
    borderRadius="6px"
    bg={matched ? "green.50" : "gray.40"}
    color={matched ? "green.primary" : "white"}
    aria-label={matched ? "matched" : "not matched"}
  >
    {matched ? (
      <FiCheck size={11} strokeWidth={3} />
    ) : (
      <FiX size={10} strokeWidth={3} />
    )}
  </Box>
);

/**
 * The three decision buttons, in escalating order of seriousness.
 *
 * A tick is deliberately not used for True Match: it reads as "approved, all
 * good", which is the opposite of what a confirmed sanctions or PEP hit means.
 */
export const MATCH_ACTION_BUTTONS = [
  {
    key: "view",
    status: "unknown",
    label: "Unknown",
    description: "You are clearing the decision on this match.",
    Icon: FaQuestion,
    iconSize: 11,
    activeBg: "gray.50",
    activeBorder: "gray.50",
  },
  {
    key: "reject",
    status: "false_positive",
    label: "False Positive",
    description:
      "You are recording that this watchlist entry is not the subject.",
    Icon: FiX,
    iconSize: 16,
    activeBg: "green.primary",
    activeBorder: "green.primary",
  },
  {
    key: "accept",
    status: "true_match",
    label: "True Match",
    description: "You are recording that this watchlist entry is the subject.",
    Icon: FiAlertTriangle,
    iconSize: 14,
    activeBg: "red.40",
    activeBorder: "red.40",
  },
];

export const MATCH_STATUS_ACTIONS: Record<
  string,
  { status: "true_match" | "false_positive" | "unknown"; label: string }
> = {
  view: { status: "unknown", label: "Unknown" },
  reject: { status: "false_positive", label: "False Positive" },
  accept: { status: "true_match", label: "True Match" },
};

/**
 * A quiet flag naming one thing this match was picked up for.
 *
 * Deliberately understated and without a count: the strip answers "what kind of
 * hit is this" at a glance, and the tabs carry the detail. Only categories with
 * hits appear, so the strip reads as a list of flags rather than a scorecard.
 */
export const SegmentChip = styled(Box)`
  ${({ theme: { colors, fontFamily, fontSizes, fontWeights, radii, space } }) => `
    display: inline-flex;
    align-items: center;
    font-family: ${fontFamily};
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.medium};
    line-height: 1;
    letter-spacing: 0.02em;
    color: ${colors.gray[60]};
    background: ${colors.gray[10]};
    border-radius: ${radii[0]};
    padding: ${space[0]} ${space[1]};
    white-space: nowrap;
  `}
`;

/**
 * A provenance flag on a match card, such as NEW RESULT.
 *
 * Takes the same treatment as the ALIAS marker rather than the segment chip,
 * because both annotate where something came from. A segment chip names a kind
 * of risk, and giving these that treatment would make them read as a watchlist
 * category the subject had been flagged under.
 */
export const AnnotationBadge = styled(Box)`
  ${({ theme: { colors, fontFamily, fontSizes, fontWeights, radii, space } }) => `
    display: inline-flex;
    align-items: center;
    font-family: ${fontFamily};
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    line-height: 1;
    letter-spacing: 0.04em;
    color: ${colors.orange[50]};
    background: ${colors.orange.primary};
    border-radius: ${radii[0]};
    padding: ${space[0]} ${space[1]};
    white-space: nowrap;
  `}
`;

/**
 * Aliases, with the one that produced the match marked and listed first.
 *
 * Transliterations are separated and collapsed: Putin carries nine non-Latin
 * renderings and Trump eight, none of which can have matched a Latin search.
 */
export const AliasList = ({
  aliases,
  transliterations,
  matchedAlias,
}: {
  aliases: DisplayAlias[];
  transliterations: DisplayAlias[];
  matchedAlias: DisplayAlias | null;
}) => {
  const [showTransliterations, setShowTransliterations] = useState(false);

  const ordered = [...aliases].sort(
    (a, b) =>
      Number(b.full === matchedAlias?.full) -
      Number(a.full === matchedAlias?.full),
  );

  return (
    <Box>
      {ordered.map((alias, i) => {
        const isMatched = alias.full === matchedAlias?.full;
        return (
          <Flex key={i} alignItems="center" gap="8" py={1}>
            <Paragraph
              mb={0}
              mt={0}
              color={isMatched ? "gray.80" : "gray.60"}
              fontWeight={isMatched ? "600" : "400"}
            >
              {alias.full}
            </Paragraph>
            {alias.type && (
              <Paragraph mb={0} mt={0} fontSize={0} color="gray.50">
                {alias.type}
              </Paragraph>
            )}
            {isMatched && (
              <Box
                bg="orange.primary"
                color="orange.50"
                borderRadius="4px"
                px={1}
                fontSize={0}
                fontWeight="600"
              >
                MATCHED
              </Box>
            )}
          </Flex>
        );
      })}

      {transliterations.length > 0 && (
        <Box mt={2} pt={2} borderTop="1px solid" borderTopColor="gray.20">
          <Button
            variant="text"
            size="small"
            px={0}
            onClick={() => setShowTransliterations((open) => !open)}
          >
            {showTransliterations ? "Hide" : "Show"} {transliterations.length}{" "}
            transliteration
            {transliterations.length === 1 ? "" : "s"}
          </Button>
          {showTransliterations && (
            <Box mt={1}>
              {transliterations.map((alias, i) => (
                <Flex key={i} alignItems="baseline" gap="8" py={1}>
                  <Paragraph mb={0} mt={0} color="gray.60">
                    {alias.full}
                  </Paragraph>
                  {alias.scriptType && (
                    <Paragraph mb={0} mt={0} fontSize={0} color="gray.50">
                      {alias.scriptType}
                    </Paragraph>
                  )}
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export const ScrollArea = styled(Box)`
  ${({ theme: { colors } }) => `
    max-height: 420px;
    overflow-y: auto;
    scrollbar-width: thin;

    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb {
      background: ${colors.gray[20]};
      border-radius: 4px;
    }
  `}
`;

/**
 * A filterable, bounded list.
 *
 * One match in the reference payloads carries up to 200 relationships and 157
 * sources. Rendered flat and unbounded they bury everything below them and make
 * the panel unusable, so every large collection gets a search box and its own
 * scroll region.
 */
export const FilterableList = ({
  items,
  placeholder,
  toText,
  children,
}: {
  items: any[];
  placeholder: string;
  toText: (item: any) => string;
  children: (visible: any[]) => React.ReactNode;
}) => {
  const [term, setTerm] = useState("");
  const query = term.trim().toLowerCase();
  const visible = query
    ? items.filter((item) => toText(item).toLowerCase().includes(query))
    : items;

  return (
    <Box>
      {items.length > 8 && (
        <Box mb={2}>
          <Input
            value={term}
            onChange={(e: any) => setTerm(e.target.value)}
            placeholder={placeholder}
            name="filter"
          />
          {query && (
            <Paragraph fontSize={0} color="gray.50" mt={1} mb={0}>
              {visible.length} of {items.length}
            </Paragraph>
          )}
        </Box>
      )}
      {visible.length === 0 ? (
        <Paragraph color="gray.50" mb={0}>
          Nothing matches that filter.
        </Paragraph>
      ) : (
        <ScrollArea>{children(visible)}</ScrollArea>
      )}
    </Box>
  );
};

/**
 * One source: domain, full URL and date modified.
 *
 * A grid rather than a flex row, and `minmax(0, 1fr)` rather than `1fr`,
 * because source URLs run to 280 characters. A flex or grid child will not
 * shrink below its content by default, so an unbreakable URL widens the whole
 * card instead of being truncated. `minmax(0, ...)` is what actually permits
 * the ellipsis to take effect.
 */
export const SourceRow = styled.div`
  ${({ theme: { space } }) => `
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: baseline;
    gap: ${space[2]};
    padding: ${space[0]} 0;
  `}
`;

export const SourceMain = styled.div`
  min-width: 0;
  overflow: hidden;
`;

/**
 * The result of a screening that returned nothing.
 *
 * A clear check is the common outcome and has to say so plainly. The record
 * still comes back, carrying an empty `matches` array and, on this path, an
 * empty `information_used` as well. Rendered as normal that produced a row of
 * dashes, a strip of four zeroes and an empty "Screening Matches" heading,
 * which reads as something having failed to load rather than as a clean result.
 */
export const NoMatchesFound = ({ type }: { type?: string }) => (
  <GrayCard mx="0px" mb={0}>
    <Flex alignItems="center" gap="16">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="32px"
        width="32px"
        flexShrink={0}
        borderRadius="8px"
        bg="green.50"
        color="green.primary"
      >
        <FiCheck size={16} strokeWidth={3} />
      </Box>
      <Box>
        <Paragraph fontWeight="600" mt={0} mb={0} color="gray.80">
          No matches found
        </Paragraph>
        <Paragraph fontSize={1} mt={1} mb={0} color="gray.60">
          {type === "entity"
            ? "This entity was screened against the LexisNexis watchlists and no potential matches were returned. There is nothing to review."
            : "This individual was screened against the LexisNexis watchlists and no potential matches were returned. There is nothing to review."}
        </Paragraph>
      </Box>
    </Flex>
  </GrayCard>
);

/** A pending check also has empty `records` — don't show it as clean yet. */
export const CheckPending = ({ type }: { type?: string }) => (
  <GrayCard mx="0px" mb={0}>
    <Flex alignItems="center" gap="16">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="32px"
        width="32px"
        flexShrink={0}
        borderRadius="8px"
        bg="gray.20"
        color="gray.60"
      >
        <FiClock size={16} strokeWidth={3} />
      </Box>
      <Box>
        <Paragraph fontWeight="600" mt={0} mb={0} color="gray.80">
          Check in progress
        </Paragraph>
        <Paragraph fontSize={1} mt={1} mb={0} color="gray.60">
          {type === "entity"
            ? "This entity's screening against the LexisNexis watchlists has not completed yet. Results will appear here once it does."
            : "This individual's screening against the LexisNexis watchlists has not completed yet. Results will appear here once it does."}
        </Paragraph>
      </Box>
    </Flex>
  </GrayCard>
);

/** Zero state for a segment with no entries. Names what was searched. */
export const EmptySegment = ({ label }: { label: string }) => (
  <Box py={3}>
    <Paragraph mb={0} mt={0} color="gray.50">
      No {label.toLowerCase()} records found for this match.
    </Paragraph>
  </Box>
);

/**
 * Deciding a match is the primary action on this screen, so it has to be a real
 * button: focusable, operable by keyboard, and announced with its pressed state.
 * These were previously divs carrying `aria-pressed`, which reads as a button to
 * a screen reader while being unreachable without a mouse.
 */
export const DecisionButton = styled.button<{
  isActive: boolean;
  activeBg: string;
  activeBorder: string;
}>`
  ${({ theme: { colors, radii }, isActive, activeBg, activeBorder }) => {
    const token = (path: string) =>
      path.split(".").reduce((value: any, key) => value?.[key], colors as any);
    return `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      padding: 0;
      cursor: pointer;
      border-radius: ${radii[0]};
      border: 1px solid ${isActive ? token(activeBorder) : colors.gray[20]};
      background: ${isActive ? token(activeBg) : colors.white};
      color: ${isActive ? colors.white : colors.gray[50]};
      transition: background 0.15s ease, border-color 0.15s ease;

      &:hover {
        border-color: ${isActive ? token(activeBorder) : colors.gray[40]};
      }

      &:focus-visible {
        outline: 2px solid ${colors.blue.primary};
        outline-offset: 2px;
      }
    `;
  }}
`;

export const MatchActionButtons = ({
  currentStatus,
  onSelect,
}: {
  currentStatus?: string;
  onSelect: (key: string) => void;
}) => (
  <Flex gap="8">
    {MATCH_ACTION_BUTTONS.map((button) => {
      const isActive = currentStatus === button.status;
      return (
        <Tooltip
          key={button.key}
          position="top"
          content={
            <>
              <strong>{button.label}</strong>
              <br />
              {button.description}
            </>
          }
        >
          <DecisionButton
            type="button"
            isActive={isActive}
            activeBg={button.activeBg}
            activeBorder={button.activeBorder}
            aria-label={`${button.label}: ${button.description}`}
            aria-pressed={isActive}
            onClick={(e: any) => {
              // the buttons sit inside the match card's expansion panel
              // header, so a click must not also toggle the card open or
              // closed
              e.stopPropagation();
              onSelect(button.key);
            }}
          >
            <button.Icon size={button.iconSize} strokeWidth={2.5} />
          </DecisionButton>
        </Tooltip>
      );
    })}
  </Flex>
);

export const SegmentEntry = ({ entry }: { entry: Record<string, any> }) => (
  <GrayCard mx="0px" mb={2}>
    {Object.entries(entry)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      )
      .map(([key, value]) => (
        <Box key={key} mb={1}>
          <CheckHeading color="gray.60" fontWeight="500">
            {formatLabel(key)}
          </CheckHeading>
          <Paragraph mt={1} mb={0} color="gray.80" fontWeight="500">
            {renderValue(value)}
          </Paragraph>
        </Box>
      ))}
  </GrayCard>
);

export const Pill = ({ label }: { label: string }) => (
  <Box
    bg="gray.20"
    color="gray.50"
    borderRadius="16px"
    px={2}
    py={0}
    fontSize={0}
    fontWeight="500"
    fontFamily="'Inter', sans-serif"
    style={{ whiteSpace: "nowrap" }}
  >
    {label || "-"}
  </Box>
);

export const AdverseMediaSegmentTable = ({ entries }: { entries: any[] }) => (
  <DataTable>
    <TableHead></TableHead>
    <TableBody>
      <CheckTableHeaderRow>
        <CheckTableCell>Description</CheckTableCell>
        <CheckTableCell>Subcategory</CheckTableCell>
      </CheckTableHeaderRow>
      {entries.map((entry, i) => (
        <TableRow key={i}>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {entry?.description || "-"}
          </TableCell>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {(entry?.sub_categories || []).length
              ? entry.sub_categories.map((sub: string, j: number) => (
                  <Paragraph
                    key={j}
                    mt={j === 0 ? 0 : 1}
                    mb={0}
                    color="gray.60"
                  >
                    {sub}
                  </Paragraph>
                ))
              : "-"}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </DataTable>
);


/**
 * PEP roles as one scannable table rather than a card per entry.
 *
 * Putin carries 14 PEP entries; as expanded cards of ten fields each that is an
 * unreadable wall. Only a few fields discriminate, and the two that matter most
 * are `status` and `is_primary`: an inactive deputy role from 1996 carries very
 * different weight from an active head of state role, and previously both
 * looked identical.
 */
export const PepSegmentTable = ({ entries }: { entries: any[] }) => (
  <DataTable>
    <TableHead></TableHead>
    <TableBody>
      <CheckTableHeaderRow>
        <CheckTableCell>Role</CheckTableCell>
        <CheckTableCell>Institution</CheckTableCell>
        <CheckTableCell>Country</CheckTableCell>
        <CheckTableCell>Period</CheckTableCell>
        <CheckTableCell>Status</CheckTableCell>
      </CheckTableHeaderRow>
      {entries.map((entry, i) => {
        const from = formatPepDate(entry?.effective_date);
        const to = formatPepDate(entry?.expiration_date);
        const period =
          from === "-" && to === "-"
            ? "-"
            : `${from === "-" ? "?" : from} to ${to === "-" ? "present" : to}`;

        return (
          <TableRow key={i}>
            <TableCell color="gray.80" borderBottom="none" verticalAlign="top">
              <Paragraph mt={0} mb={0} fontWeight="500" color="gray.80">
                {entry?.governing_role || entry?.country_role || "-"}
              </Paragraph>
              {(entry?.sub_categories || []).length > 0 && (
                <Flex gap="4" mt={1} flexWrap="wrap">
                  {entry.sub_categories.map((category: string, j: number) => (
                    <SegmentChip key={j}>{category}</SegmentChip>
                  ))}
                </Flex>
              )}
            </TableCell>
            <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
              {entry?.governing_institution || "-"}
            </TableCell>
            <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
              {entry?.country || "-"}
              {entry?.admin_level ? (
                <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
                  {entry.admin_level}
                </Paragraph>
              ) : null}
            </TableCell>
            <TableCell
              color="gray.60"
              borderBottom="none"
              verticalAlign="top"
              style={{ whiteSpace: "nowrap" }}
            >
              {period}
            </TableCell>
            <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
              <Flex gap="4" flexWrap="wrap" alignItems="center">
                <Pill label={entry?.status} />
                {entry?.is_primary && <SegmentChip>Primary</SegmentChip>}
              </Flex>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </DataTable>
);

/**
 * Sanctions and enforcements share an identical shape:
 * `{country, source, sub_categories[], date_modified}`.
 *
 * Sub-category distinguishes one kind of listing from another, and date
 * modified tells the reviewer how current the listing is. Both were previously
 * dropped from the sanctions view.
 */
export const ListingSegmentTable = ({ entries }: { entries: any[] }) => (
  <DataTable>
    <TableHead></TableHead>
    <TableBody>
      <CheckTableHeaderRow>
        <CheckTableCell>Country</CheckTableCell>
        <CheckTableCell>Source</CheckTableCell>
        <CheckTableCell>Subcategory</CheckTableCell>
        <CheckTableCell>Last Updated</CheckTableCell>
      </CheckTableHeaderRow>
      {entries.map((entry, i) => (
        <TableRow key={i}>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {entry?.country || "-"}
          </TableCell>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {entry?.source || "-"}
          </TableCell>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {renderValue(entry?.sub_categories)}
          </TableCell>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {formatSourceDate(entry?.date_modified)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </DataTable>
);

export const SoeSegmentTable = ({ entries }: { entries: any[] }) => (
  <>
    {entries.map((entry, i) => (
      <Box key={i} mb={3}>
        <DataTable>
          <TableHead></TableHead>
          <TableBody>
            <CheckTableHeaderRow>
              <CheckTableCell>Status</CheckTableCell>
              <CheckTableCell>Subcategory</CheckTableCell>
              <CheckTableCell>Total Ownership %</CheckTableCell>
              <CheckTableCell>Domain</CheckTableCell>
            </CheckTableHeaderRow>
            <TableRow>
              <TableCell
                color="gray.60"
                borderBottom="none"
                verticalAlign="top"
              >
                <Pill label={entry?.is_active ? "Active" : "Inactive"} />
              </TableCell>
              <TableCell
                color="gray.60"
                borderBottom="none"
                verticalAlign="top"
              >
                {renderValue(entry?.sub_categories)}
              </TableCell>
              <TableCell
                color="gray.60"
                borderBottom="none"
                verticalAlign="top"
              >
                {entry?.total_ownership_country_percentage !== undefined &&
                entry?.total_ownership_country_percentage !== null
                  ? `${entry.total_ownership_country_percentage}%`
                  : "-"}
              </TableCell>
              <TableCell
                color="gray.60"
                borderBottom="none"
                verticalAlign="top"
              >
                {(entry?.domain || []).length
                  ? entry.domain.map((domain: string, j: number) => (
                      <Paragraph
                        key={j}
                        mt={j === 0 ? 0 : 1}
                        mb={0}
                        color="gray.60"
                      >
                        {domain}
                      </Paragraph>
                    ))
                  : "-"}
              </TableCell>
            </TableRow>
          </TableBody>
        </DataTable>

        {(entry?.country_associations || []).length > 0 && (
          <DataTable>
            <TableHead></TableHead>
            <TableBody>
              <CheckTableHeaderRow>
                <CheckTableCell>Ownership Country</CheckTableCell>
                <CheckTableCell>Ownership Country %</CheckTableCell>
              </CheckTableHeaderRow>
              {entry.country_associations.map(
                (association: any, j: number) => (
                  <TableRow key={j}>
                    <TableCell
                      color="gray.60"
                      borderBottom="none"
                      verticalAlign="top"
                    >
                      {association?.country || "-"}
                    </TableCell>
                    <TableCell
                      color="gray.60"
                      borderBottom="none"
                      verticalAlign="top"
                    >
                      {association?.ownership_percentage !== undefined &&
                      association?.ownership_percentage !== null
                        ? `${association.ownership_percentage}%`
                        : "-"}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </DataTable>
        )}
      </Box>
    ))}
  </>
);

export const AdditionalSegmentsTable = ({ entries }: { entries: any[] }) => (
  <DataTable>
    <TableHead></TableHead>
    <TableBody>
      <CheckTableHeaderRow>
        <CheckTableCell>Subcategory</CheckTableCell>
        <CheckTableCell>Source</CheckTableCell>
      </CheckTableHeaderRow>
      {entries.map((entry, i) => (
        <TableRow key={i}>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {entry?.subcategory || renderValue(entry?.sub_categories)}
          </TableCell>
          <TableCell color="gray.60" borderBottom="none" verticalAlign="top">
            {entry?.source || "-"}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </DataTable>
);

export const SEGMENT_TABLES: Record<
  string,
  (props: { entries: any[] }) => JSX.Element
> = {
  adverse_media: AdverseMediaSegmentTable,
  pep: PepSegmentTable,
  sanctions: ListingSegmentTable,
  enforcements: ListingSegmentTable,
  soes: SoeSegmentTable,
  other: AdditionalSegmentsTable,
};
export const StatusPill = ({
  decision,
  status,
  statusDisplay,
}: {
  decision?: unknown;
  status?: unknown;
  statusDisplay?: unknown;
}) => {
  const statusKey = status == null ? "" : String(status);
  // `decision` is an object carrying the audit trail, not a status string.
  // Its `status` is the display form ("Unknown"), so it is only a fallback.
  const decisionKey =
    typeof decision === "string"
      ? decision
      : String((decision as any)?.status ?? "")
          .toLowerCase()
          .replace(/\s+/g, "_");
  const key = statusKey || decisionKey || "unknown";
  const style = DECISION_COLORS[key] || DECISION_COLORS.unknown;
  const label =
    statusDisplay != null && statusDisplay !== ""
      ? String(statusDisplay)
      : DECISION_LABELS[key] || formatLabel(key);

  return (
    <Box
      bg={style.bg}
      color={style.color}
      borderRadius="12px"
      px={2}
      py={1}
      fontSize={0}
      fontWeight="600"
      style={{ whiteSpace: "nowrap", fontFamily: "Inter, sans-serif" }}
    >
      {label}
    </Box>
  );
};
